import { useCallback, useEffect, useRef, useState } from 'react'

import {
  markAllSeenInList,
  markOneSeenInList,
  notificationsApi,
} from '../services/notificationsApi'
import type { AppNotification } from '../types/notifications'
import { NOTIFICATIONS_SEEN_EVENT } from './useNotifications'

const PAGE_SIZE = 30

export interface UseNotificationHistoryResult {
  items: AppNotification[]
  /** First-page load (drawer just opened / reload). */
  loading: boolean
  /** A "Load earlier" page is in flight. */
  loadingMore: boolean
  error: boolean
  /** True while the last page came back full - another page may exist. */
  hasMore: boolean
  loadMore: () => Promise<void>
  reload: () => Promise<void>
  markSeen: (id: number) => Promise<void>
  markAllSeen: () => Promise<void>
}

/**
 * Paginated history for the notification drawer - the full-list sibling of
 * `useNotifications` (which owns the bell badge + recent-list). Pages with the
 * `before_id` keyset cursor, and marks seen optimistically, broadcasting
 * `NOTIFICATIONS_SEEN_EVENT` so the bell badge follows. It does not *listen*
 * to that event: the bell popup is always closed while the drawer is open
 * (the footer link closes it), so there is no concurrent bell mutation to
 * reconcile, and a reopen reloads fresh.
 */
export function useNotificationHistory(enabled: boolean): UseNotificationHistoryResult {
  const [items, setItems] = useState<AppNotification[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(false)
  const [hasMore, setHasMore] = useState(false)

  // Epoch token: bumped on every open/close transition and on unmount, so a
  // request that resolves after the drawer was closed/reopened is discarded
  // (it captured an older epoch). A shared boolean ref can't do this - it
  // would be reset to "live" by the very effect run that should cancel it.
  const epochRef = useRef(0)

  const reload = useCallback(async () => {
    const epoch = epochRef.current
    setLoading(true)
    setError(false)
    try {
      const rows = await notificationsApi.list({ limit: PAGE_SIZE })
      if (epoch !== epochRef.current) return
      setItems(rows)
      setHasMore(rows.length === PAGE_SIZE)
    } catch {
      if (epoch === epochRef.current) setError(true)
    } finally {
      if (epoch === epochRef.current) setLoading(false)
    }
  }, [])

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return
    const cursor = items[items.length - 1]?.id
    if (cursor == null) return
    const epoch = epochRef.current
    setLoadingMore(true)
    try {
      const rows = await notificationsApi.list({ limit: PAGE_SIZE, beforeId: cursor })
      if (epoch !== epochRef.current) return
      setItems((prev) => [...prev, ...rows])
      setHasMore(rows.length === PAGE_SIZE)
    } catch {
      if (epoch === epochRef.current) setError(true)
    } finally {
      if (epoch === epochRef.current) setLoadingMore(false)
    }
  }, [items, hasMore, loading, loadingMore])

  // Load the first page when the drawer opens; reset when it closes so a
  // reopen starts clean (and so a left-mounted hook never holds stale rows).
  // Bumping the epoch here + in cleanup invalidates any in-flight request from
  // the previous cycle.
  useEffect(() => {
    epochRef.current += 1
    if (enabled) {
      reload()
    } else {
      setItems([])
      setHasMore(false)
      setError(false)
    }
    return () => {
      epochRef.current += 1
    }
  }, [enabled, reload])

  const markSeen = useCallback(
    async (id: number) => {
      setItems((prev) => markOneSeenInList(prev, id, new Date().toISOString()))
      try {
        await notificationsApi.markSeen(id)
        window.dispatchEvent(new CustomEvent(NOTIFICATIONS_SEEN_EVENT))
      } catch {
        reload()
      }
    },
    [reload],
  )

  const markAllSeen = useCallback(async () => {
    setItems((prev) => markAllSeenInList(prev, new Date().toISOString()))
    try {
      await notificationsApi.markAllSeen()
      window.dispatchEvent(new CustomEvent(NOTIFICATIONS_SEEN_EVENT))
    } catch {
      reload()
    }
  }, [reload])

  return {
    items,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    reload,
    markSeen,
    markAllSeen,
  }
}
